import { AlertDialogFooter, useColorMode } from '@chakra-ui/react'
import {
    Body,
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogOverlay,
    FormProvider,
    Spinner,
    useYupForm,
    yup
} from '@missionlabs/react'
import { User } from '@missionlabs/types'
import { userAddressSchema } from 'features/settings/pages/MyDetails/MyDetails'
import { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Address } from 'shared/components/Address'
import { useGetUserData } from 'shared/hooks/useGetUserData'
import { useOnLogout } from 'shared/hooks/useOnLogout'
import { useUpdateUserMutation } from 'shared/store'

const emergencyDetailsCaptureSchema = yup.object({
    address: userAddressSchema
})

interface EmergencyCaptureModalProps {}

export const EmergencyCaptureModal: FC<EmergencyCaptureModalProps> = () => {
    const ref = useRef<HTMLButtonElement>(null)
    const { t } = useTranslation()
    const { colorMode } = useColorMode()
    const { handleLogout } = useOnLogout()

    const location = useLocation()
    const navigate = useNavigate()

    const {
        user,
        __meta: {
            user: { isLoading: isUserLoading }
        }
    } = useGetUserData()

    const [updateUser, { isError, isLoading }] = useUpdateUserMutation()

    const methods = useYupForm<Pick<User, 'address'>>(emergencyDetailsCaptureSchema, {
        shouldFocusError: false,
        values: { address: user?.address },
        mode: 'onChange'
    })

    const {
        formState: { isValid },
        resetField
    } = methods

    useEffect(() => {
        if (user?.address) resetField('address', { defaultValue: user?.address })
    }, [user?.address, resetField])

    const handleSubmit = async (data: Pick<User, 'address'>) => {
        if (!user) return

        try {
            await updateUser({
                ID: user.ID,
                address: data.address
            }).unwrap()

            navigate('/home', { replace: true, state: { redirect: location.pathname } })
        } catch (e) {
            console.error(e)
        }
    }

    if (isUserLoading) return <Spinner size="sm" />

    return (
        <Dialog leastDestructiveRef={ref} isOpen={true} onClose={() => {}} size="md">
            <DialogOverlay />
            <DialogContent>
                <DialogHeader>{t('emergencyDetails.addressConfirmation')}</DialogHeader>
                <DialogBody>{t('emergencyDetails.addressConfirmationReason')}</DialogBody>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(handleSubmit)}>
                        <DialogBody>
                            <Address />
                            {isError && (
                                <Body
                                    role="alert"
                                    size="sm"
                                    mt="16px"
                                    color={`${colorMode}.alerts.red`}
                                >
                                    {t('emergencyDetails.unableToUpdateAddress')}
                                </Body>
                            )}
                        </DialogBody>
                        <AlertDialogFooter>
                            <Button
                                size="lg"
                                width="full"
                                variant="negatory"
                                type="button"
                                onClick={handleLogout}
                            >
                                {t('Log out')}
                            </Button>
                            <Button
                                ref={ref}
                                size="lg"
                                width="full"
                                variant="creationary"
                                isDisabled={!isValid || isLoading || isUserLoading}
                                type="submit"
                            >
                                {t('Confirm')}
                            </Button>
                        </AlertDialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
